import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { isPreviewEnv } from "@/lib/isPreviewEnv";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isChunkError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Detect chunk loading errors (happens after deployment with new hashes)
    const msg = error.message || '';
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes('is not defined') ||
      msg.includes('Unexpected token') ||
      msg.includes('Importing a module script failed');
    
    return { hasError: true, error, isChunkError };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    // Error reporting and stale-chunk recovery are browser-only. React can
    // invoke componentDidCatch during SSR, so never evaluate modules that
    // touch window, navigator, or service workers on the server.
    if (typeof window === "undefined") return;

    // Error reporting and stale-chunk recovery are only needed after a render
    // failure. Keep their backend/PWA dependencies out of the first-screen graph.
    void import("@/lib/analytics").then(({ captureError }) => {
      captureError(error, {
        componentStack: errorInfo.componentStack,
        isChunkError: this.state.isChunkError,
        source: "ErrorBoundary",
      });
    });

    // A preview rebuild can briefly invalidate an in-flight module request.
    // Vite already reconnects/reloads once; adding production self-healing here
    // caused a second hard reload that made the preview repeatedly disappear.
    if (this.state.isChunkError && !import.meta.env.DEV && !isPreviewEnv()) {
      void import("@/lib/pwa").then(({ hasAttemptedSelfHeal, selfHealAndReload }) => {
        if (!hasAttemptedSelfHeal()) void selfHealAndReload();
      });
    }
  }

  private handleReload = () => {
    // Manual recovery also goes through self-heal in case the first
    // attempt's SW unregister/cache purge raced with the reload.
    if (typeof window === "undefined") return;
    void import("@/lib/pwa").then(({ selfHealAndReload }) =>
      selfHealAndReload().then((ok) => {
        if (!ok) window.location.reload();
      }),
    );
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-dvh bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <div className="flex justify-center">
              <AlertCircle className="h-16 w-16 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Something went wrong</h2>
            <p className="text-muted-foreground">
              {this.state.isChunkError 
                ? "The app was updated. Please refresh to load the latest version."
                : (this.state.error?.message || "An unexpected error occurred")}
            </p>
            {this.state.isChunkError ? (
              <Button onClick={this.handleReload} className="w-full gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh Page
              </Button>
            ) : (
              <Button
                onClick={() => {
                  this.setState({ hasError: false, error: null, isChunkError: false });
                  window.location.href = "/learning-journeys";
                }}
                className="w-full"
              >
                Go to Learning Journey
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
